/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/member-delimiter-style */
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ApiService } from "./api.service";
import { StartupInfos_PeripheriqueSynchronisation, STATUS_PERIPHERIQUE, DeviceInfos, AppInfos, UpdateInfos, Model_SynchronisationTask, TYPE_SYNCHRONISATION_TASK, TYPE_SYNCHRONISATION } from './app.models';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import { UnitOfWorkOfflineDao } from './core-api/daos/unitofwork-offline.dao';
import { ABWToastService } from './shared/helpers/abw-toast.service';
import { PlatformService } from './core-api/platform/platform.service';
import { CustomLoaderService } from './shared/custom-loader/custom-loader.service';
import { SynchronisationService } from './synchronisation.service';

export enum APP_MODE {
    ONLINE_MODE = 0,
    OFFLINE_MODE = 1
}

@Injectable({
    providedIn: 'root'
})
export class AppService {
    appVersion$: BehaviorSubject<AppInfos> = new BehaviorSubject({ APP_VERSION_ID: '0.0.0', APP_VERSION_DISPLAY: 'X', APP_COMMIT_SHA: 'Z', CODE_SOCIETE: '', NOM_SOCIETE: '' });
    appMode$ = new BehaviorSubject(APP_MODE.ONLINE_MODE);
    btPrinterDeviceConnected: any = null;

    set AppMode(_mode: APP_MODE) { localStorage.setItem(environment.LS_ABWORKFLOW_CORE_APP_MODE, _mode.toString()); }
    get AppMode(): APP_MODE {
        const lsAppModeStr = localStorage.getItem(environment.LS_ABWORKFLOW_CORE_APP_MODE);
        let parsedAppMode = APP_MODE.ONLINE_MODE;
        if (!!lsAppModeStr && lsAppModeStr !== 'undefined' && lsAppModeStr !== 'null') { 
            parsedAppMode = parseInt(lsAppModeStr) == APP_MODE.OFFLINE_MODE ? APP_MODE.OFFLINE_MODE : APP_MODE.ONLINE_MODE;
        }
        return parsedAppMode;
    }

    set AppVersion(_appVersion: AppInfos) { 
        localStorage.setItem(environment.LS_ABWORKFLOW_APP_VERSION, JSON.stringify(_appVersion)); 
    }
    get AppVersion(): AppInfos {
        const lsAppVersionStr = localStorage.getItem(environment.LS_ABWORKFLOW_APP_VERSION);
        let parsedAppVersion: any = { APP_VERSION_ID: '0.0.0', APP_VERSION_DISPLAY: '', APP_COMMIT_SHA: '', };
        if (!!lsAppVersionStr && lsAppVersionStr !== 'undefined' && lsAppVersionStr !== 'null') {
            try {
                parsedAppVersion = JSON.parse(lsAppVersionStr);
            } catch(e) {
            }
        }

        return parsedAppVersion;
    }
    
    constructor(
        private _httpClient: HttpClient, 
        private _apiService: ApiService,
        private _unitOfWork: UnitOfWorkOfflineDao,
        private _platformService: PlatformService,
        private _toastService: ABWToastService,
        private _customLoaderService: CustomLoaderService,
        private _synchronisationService: SynchronisationService
    ) {
        this._checkAppMode();
    }

    private _checkAppMode() {
        const appMode = this.AppMode;
        this.appMode$.next(appMode);
    }

    getLatestAppVersionInfos() {
        return this._httpClient.get<UpdateInfos>(this._apiService.endPoints.GetLatestAppInfos);
    }

    getAppVersion() {
        return new Promise<void>((resolve, reject) => {
            this._platformService.getAppVersion().then(_appVersions => {
                this.AppVersion = _appVersions;
                this.appVersion$.next(_appVersions);
                this._customLoaderService.hide();
                resolve();
            }, (_error) => {
                console.error(_error);
                reject(_error);
            })
        });
    }

    doSynchronizeApp() {
        return new Promise(async (resolve, reject) => {
            let promises: any = [];
            if (this.AppMode == APP_MODE.ONLINE_MODE) {
                const startupInfos = await this._httpClient.get<StartupInfos_PeripheriqueSynchronisation>(this._apiService.endPoints.doGetStartupInfosPeripheriqueSynchronisation_Peripherique).toPromise();
                promises = Promise.all([
                    this._unitOfWork.workflowDao.doSyncWorkflowsAction(startupInfos.WORKFLOWS),
                    this._unitOfWork.datasourcesDao.doSyncDatasourcesAction(startupInfos.DATASOURCES),
                    this._unitOfWork.envVarUserDao.doSyncEnvironmentVariablesUserAction(startupInfos.VARIABLES_ENVIRONNEMENT)
                ]);
            } else {
                console.debug('Début de synchronisation des données locales vers le serveur...');
                promises = this.doSyncLocalDatasToRemote();
            }
            promises.then(() => {
                const dateSynchronisation = moment().format();
                localStorage.setItem(environment.LS_ABWORKFLOW_LAST_DATE_SYNCHRONISATION, dateSynchronisation);
                console.debug('Synchronisation des données locales vers le serveur effectuée !');
                resolve();
            }).catch((err) => {
                console.debug('Erreur lors de la synchronisation des données locales vers le serveur...', err);
                console.error(err);
                this._toastService.showToast(err);
                reject(err);
            });
        });
    }

    async doSyncLocalDatasToRemote() {
        const notSyncedResponses = await this._unitOfWork.responseWorkflowDao.doGetResponsesAction({ SYNCED: false });
        const notSyncedEnvVarsUser = await this._unitOfWork.envVarUserDao.doGetEnvironmentVariablesUserAction({ SYNCED: false });
        const notSyncedLogEnvVarsUser = await this._unitOfWork.logEnvVarUserOfflineDao.doGetLogsEnvVarUserAction({ SYNCED: false });
        let syncTasks: Model_SynchronisationTask[] = [];
        syncTasks = syncTasks.concat(notSyncedResponses.map(response => ({ TYPE_SYNCHRONISATION_TASK: TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_RESPONSE_WORKFLOW, SYNCHRONISATION_TASK_PARAMS: { RESPONSE_WORKFLOW_GUID: response.GUID_SYNCHRONISATION } }) ));
        syncTasks = syncTasks.concat(notSyncedEnvVarsUser.map(envVarUser => ({ TYPE_SYNCHRONISATION_TASK: TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_VARIABLE_ENVIRONNEMENT, SYNCHRONISATION_TASK_PARAMS: { NOM_VARIABLE: envVarUser.NOM_VARIABLE, VALEUR_VARIABLE: envVarUser.VALEUR_VARIABLE } }) ));
        syncTasks = syncTasks.concat(notSyncedLogEnvVarsUser.map(logEnvVarUser => ({ TYPE_SYNCHRONISATION_TASK: TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_LOG_VARIABLE, SYNCHRONISATION_TASK_PARAMS: { LOG_VARIABLE_ENVIRONNEMENT_USER_GUID: logEnvVarUser.GUID_SYNCHRONISATION, BEFORE_VALUE: logEnvVarUser.BEFORE_VALUE, CURRENT_VALUE: logEnvVarUser.CURRENT_VALUE } }) ));
        if (syncTasks.length > 0) {
            const initiatedSynchronisation = await this._synchronisationService.doInitSynchronisationAction({ TYPE_SYNCHRONISATION: TYPE_SYNCHRONISATION.PERIPHERIQUE, SYNCHRONISATION_TASKS: syncTasks }).toPromise();
            const responsesSyncTasks = initiatedSynchronisation.SYNCHRONISATION_TASKS.filter(t => t.TYPE_SYNCHRONISATION_TASK == TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_RESPONSE_WORKFLOW);
            for (var t in responsesSyncTasks) {
                const task = responsesSyncTasks[t];
                if (!!task.SYNCHRONISATION_TASK_PARAMS && !!task.SYNCHRONISATION_TASK_PARAMS.RESPONSE_WORKFLOW_GUID) {
                    const syncData = notSyncedResponses.find(r => r.GUID_SYNCHRONISATION == task.SYNCHRONISATION_TASK_PARAMS.RESPONSE_WORKFLOW_GUID);
                    if (!!syncData) {
                        try {
                            await this._synchronisationService.doProcessSynchronisationTaskAction(task.GUID_SYNCHRONISATION_TASK, syncData).toPromise();
                            syncData.SYNCED = true;
                            await this._unitOfWork.responseWorkflowDao.doSaveResponsesAction(syncData);
                        } catch(e) {}
                    }
                }
            }

            const envVarsUserSyncTasks = initiatedSynchronisation.SYNCHRONISATION_TASKS.filter(t => t.TYPE_SYNCHRONISATION_TASK == TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_VARIABLE_ENVIRONNEMENT);
            for (var t in envVarsUserSyncTasks) {
                const task = envVarsUserSyncTasks[t];
                if (!!task.SYNCHRONISATION_TASK_PARAMS && !!task.SYNCHRONISATION_TASK_PARAMS.NOM_VARIABLE) {
                    const syncData = notSyncedEnvVarsUser.find(r => r.NOM_VARIABLE == task.SYNCHRONISATION_TASK_PARAMS.NOM_VARIABLE);
                    if (!!syncData) {
                        await this._synchronisationService.doProcessSynchronisationTaskAction(task.GUID_SYNCHRONISATION_TASK, syncData).toPromise();
                        syncData.SYNCED = true;
                        await this._unitOfWork.envVarUserDao.doSaveEnvironmentVariablesUserAction(syncData);
                    }
                }
            }

            const logEnvVarsUserSyncTasks = initiatedSynchronisation.SYNCHRONISATION_TASKS.filter(t => t.TYPE_SYNCHRONISATION_TASK == TYPE_SYNCHRONISATION_TASK.PERIPHERIQUE_LOG_VARIABLE);
            for (var t in logEnvVarsUserSyncTasks) {
                const task = logEnvVarsUserSyncTasks[t];
                if (!!task.SYNCHRONISATION_TASK_PARAMS && !!task.SYNCHRONISATION_TASK_PARAMS.LOG_VARIABLE_ENVIRONNEMENT_USER_GUID) {
                    const syncData = notSyncedLogEnvVarsUser.find(r => r.GUID_SYNCHRONISATION == task.SYNCHRONISATION_TASK_PARAMS.LOG_VARIABLE_ENVIRONNEMENT_USER_GUID);
                    if (!!syncData) {
                        await this._synchronisationService.doProcessSynchronisationTaskAction(task.GUID_SYNCHRONISATION_TASK, syncData).toPromise();
                        syncData.SYNCED = true;
                        await this._unitOfWork.logEnvVarUserOfflineDao.doSaveLogEnvVarUserAction(syncData);
                    }
                }
            }
        }
    }

    doClearOfflineDatabaseAction(deleteCoreDatas: boolean): Promise<void> {
        return new Promise((resolve, reject) => {
            Promise.all([
                this._unitOfWork.responseWorkflowDao.doSyncResponsesAction([]),
                this._unitOfWork.envVarUserDao.doSyncEnvironmentVariablesUserAction([]),
                this._unitOfWork.logEnvVarUserOfflineDao.doSyncLogsEnvVarUserAction([]),
            ]).then(async () => {
                if (deleteCoreDatas === true) {
                    await this._unitOfWork.datasourcesDao.doSyncDatasourcesAction([]);
                    await this._unitOfWork.workflowDao.doSyncWorkflowsAction([]);
                }
                resolve();
            }, err => {
                console.log(err);
                reject(err);
            });
        });
    }

    doCheckStatusPeripheriqueAction(_deviceInfos: DeviceInfos): Promise<STATUS_PERIPHERIQUE> {
        return this._httpClient.post<STATUS_PERIPHERIQUE>(this._apiService.coreEndpointUrl + '/api/Peripherique/doCheckStatusPeripheriqueAction_Peripherique/' + _deviceInfos.androidID, {
            MARQUE: _deviceInfos.marque,
            MODELE: _deviceInfos.modele,
            MAC_ADDRESS: _deviceInfos.macAddress,
            ANDROID_ID: _deviceInfos.androidID,
            APP_VERSION: _deviceInfos.appVersion
        }).toPromise();
    }
}